import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, PackageIcon, SettingsIcon, CreditCardIcon, HelpCircleIcon } from 'lucide-react';
interface FAQItem {
  question: string;
  answer: string | JSX.Element;
}
interface FAQCategory {
  title: string;
  icon: React.ReactNode;
  items: FAQItem[];
}
export const FAQPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const toggleItem = (categoryIndex: number, itemIndex: number) => {
    const key = `${categoryIndex}-${itemIndex}`;
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedItems(newExpanded);
  };
  const isExpanded = (categoryIndex: number, itemIndex: number) => {
    return expandedItems.has(`${categoryIndex}-${itemIndex}`);
  };
  const faqCategories: FAQCategory[] = [{
    title: 'Getting Started',
    icon: <PackageIcon className="w-5 h-5" data-id="element-4600" />,
    items: [{
      question: 'What is DGStack?',
      answer: 'DGStack is a ready-to-use digital product marketplace template that helps you launch your own platform quickly with built-in features like user accounts, product listings, and dashboards.'
    }, {
      question: 'Do I need coding experience to use DGStack?',
      answer: "Basic technical knowledge is recommended for setup and customization. You'll need to connect your own Firebase project using the provided setup instructions. For customization, familiarity with React/TypeScript or AI coding tools is helpful."
    }, {
      question: 'What tech stack does DGStack use?',
      answer: 'DGStack is built with React, TypeScript, Firebase (Authentication, Firestore, Storage), and Tailwind CSS.'
    }, {
      question: 'What features are included?',
      answer: <div data-id="element-4601">
              <p className="mb-2" data-id="element-4602">DGStack includes:</p>
              <ul className="list-disc list-inside space-y-1 ml-2" data-id="element-4603">
                <li data-id="element-4604">
                  User authentication (email/password, Google OAuth ready)
                </li>
                <li data-id="element-4605">
                  Seller dashboard (product uploads, analytics, earnings
                  tracking)
                </li>
                <li data-id="element-4606">Buyer dashboard (purchase history, favorites, reviews)</li>
                <li data-id="element-4607">Product browsing and search</li>
                <li data-id="element-4608">
                  Shopping cart and checkout UI (payment integration required)
                </li>
                <li data-id="element-4609">User profiles and settings</li>
                <li data-id="element-4610">Dark mode support</li>
                <li data-id="element-4611">Responsive mobile design</li>
                <li data-id="element-4612">Firebase backend (database, storage, authentication)</li>
              </ul>
            </div>
    }, {
      question: 'Is this a finished product or a template?',
      answer: 'DGStack is a starter template designed to help you launch faster. You can build on top of it and expand it based on your goals.'
    }]
  }, {
    title: 'Setup & Deployment',
    icon: <SettingsIcon className="w-5 h-5" data-id="element-4613" />,
    items: [{
      question: 'Does this include Firebase setup?',
      answer: "Yes, DGStack includes Firebase integration for authentication, database (Firestore), and file storage. You'll need to create your own Firebase project and connect it using the setup instructions."
    }, {
      question: 'Can I deploy this to my own domain?',
      answer: 'Yes. DGStack can be deployed to platforms like Netlify, Vercel, or Firebase Hosting.'
    }, {
      question: 'What happens after I purchase?',
      answer: "You'll receive access to the complete source code, documentation, and setup instructions. You can download and deploy it immediately."
    }]
  }, {
    title: 'Payments & Customization',
    icon: <CreditCardIcon className="w-5 h-5" data-id="element-4614" />,
    items: [{
      question: 'How do payments work?',
      answer: 'DGStack does not include built-in payment processing. You must integrate your own payment provider (such as Stripe or others).'
    }, {
      question: 'Can I customize the platform?',
      answer: 'Yes. DGStack is fully customizable. You can modify design, layout, and features. Customization requires React/TypeScript knowledge or the use of AI coding tools.'
    }]
  }, {
    title: 'Support & Policies',
    icon: <HelpCircleIcon className="w-5 h-5" data-id="element-4615" />,
    items: [{
      question: 'What kind of support do you provide?',
      answer: 'Support is limited to basic setup guidance, basic usage questions, and bug reports related to the original template. Custom development, advanced technical support, third-party integrations, and ongoing maintenance are not included.'
    }, {
      question: 'How can I contact support?',
      answer: 'You can contact support through Gumroad messages after purchase. Additional contact details may be provided if needed.'
    }, {
      question: 'Do you offer refunds?',
      answer: 'Due to the nature of digital products, all sales are final unless otherwise stated.'
    }, {
      question: 'Can I resell or redistribute this template?',
      answer: 'This template is licensed for personal or commercial use to build your own projects. Reselling, redistributing, or sharing the original source code is not allowed.'
    }]
  }];
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-4616">
      <Navbar scrolled={scrolled} data-id="element-4617" />
      <main className="pt-24 pb-20" data-id="element-4618">
        <div className="container mx-auto px-4 md:px-6" data-id="element-4619">
          <div className="max-w-4xl mx-auto" data-id="element-4620">
            {/* Header */}
            <div className="text-center mb-12" data-id="element-4621">
              <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-800 dark:text-[#F8FAF9]" data-id="element-4622">
                Frequently Asked Questions
              </h1>
              <p className="text-xl text-gray-600 dark:text-[#B0C1BD]" data-id="element-4623">
                Everything you need to know about DGStack
              </p>
            </div>

            {/* FAQ Categories */}
            <div className="space-y-8" data-id="element-4624">
              {faqCategories.map((category, categoryIndex) => <div key={categoryIndex} className="bg-white dark:bg-[#162825] rounded-xl shadow-sm overflow-hidden" data-id="element-4625">
                  {/* Category Header */}
                  <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-6 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-4626">
                    <div className="flex items-center" data-id="element-4627">
                      <div className="text-[#2DB489] dark:text-[#4ED1A0] mr-3" data-id="element-4628">
                        {category.icon}
                      </div>
                      <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4629">
                        {category.title}
                      </h2>
                    </div>
                  </div>

                  {/* FAQ Items */}
                  <div className="divide-y divide-gray-200 dark:divide-[#1A3330]" data-id="element-4630">
                    {category.items.map((item, itemIndex) => <div key={itemIndex} data-id="element-4631">
                        <button onClick={() => toggleItem(categoryIndex, itemIndex)} className="w-full px-6 py-4 flex justify-between items-center text-left hover:bg-gray-50 dark:hover:bg-[#1A3330]/30 transition-colors" data-id="element-4632">
                          <span className="font-medium text-gray-800 dark:text-[#F8FAF9] pr-4" data-id="element-4633">
                            {item.question}
                          </span>
                          <ChevronDownIcon className={`w-5 h-5 text-gray-400 dark:text-[#7A8F8A] flex-shrink-0 transition-transform ${isExpanded(categoryIndex, itemIndex) ? 'transform rotate-180' : ''}`} data-id="element-4634" />
                        </button>
                        {isExpanded(categoryIndex, itemIndex) && <div className="px-6 pb-4 text-gray-600 dark:text-[#B0C1BD]" data-id="element-4635">
                            {typeof item.answer === 'string' ? <p data-id="element-4636">{item.answer}</p> : item.answer}
                          </div>}
                      </div>)}
                  </div>
                </div>)}
            </div>

            {/* CTA Section */}
            <div className="mt-12 text-center bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm" data-id="element-4637">
              <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4638">
                Still have questions?
              </h3>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-4639">
                We're here to help. Get in touch with our support team.
              </p>
              <Link to="/contact" className="inline-block px-6 py-3 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white font-medium rounded-lg hover:shadow-lg transition-all" data-id="element-4640">
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-4641" />
    </div>;
};